/** @jsxImportSource react */
import { useState } from 'react'
import {
  Avatar, Box, Button, Chip, Collapse, IconButton, Link, Menu, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import BoltIcon from '@mui/icons-material/Bolt'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import MergeTypeIcon from '@mui/icons-material/MergeType'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import { RESULT_LABEL, STATUS_LABEL, whenLabel } from '../meetings/meetingModel'
import { useNameOf } from './PersonInput'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { activityAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { allFields, formatValue } from './fieldValues'
import { LookupLink } from './LookupInput'
import { entityLabel, formatDate, formatRelativeTime } from '../../utils/helpers'
import { getErrorMessage } from '../../utils/apiErrors'
import { useNotify } from '../common/NotificationProvider'
import ConfirmDialog from '../common/ConfirmDialog'

const PAGE_SIZE = 20
const MAX_NOTE = 5000

/** One value of a change: names for links and people, option labels, formatted numbers and dates. */
const ChangeValue = ({ change, value, field, refs }) => {
  if (value === null || value === undefined) {
    return <Typography component="span" variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>empty</Typography>
  }
  const type = field?.fieldType || change.fieldType
  if (type === 'LOOKUP' || type === 'USER') {
    return <LookupLink refValue={refs[value] || { id: value, entityType: type === 'USER' ? 'USER' : '', title: '(unknown)', deleted: true }} />
  }
  // the field may have been removed from the form since: show the raw value
  const text = field ? formatValue(field, value) : (Array.isArray(value) ? value.join(', ') : String(value))
  return <Typography component="span" variant="body2" sx={{ wordBreak: 'break-word' }}>{text}</Typography>
}

const ChangeList = ({ changes, byKey, refs, created }) => (
  <Stack spacing={0.5} sx={{ mt: 0.75 }}>
    {changes.map((c) => (
      <Box key={c.field} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.75 }}>
        <Typography component="span" variant="body2" color="text.secondary">{byKey[c.field]?.label || c.label}:</Typography>
        {!created && (
          <>
            <Box component="span" sx={{ color: 'text.secondary', '& *': { color: 'inherit !important' } }}>
              <ChangeValue change={c} value={c.before} field={byKey[c.field]} refs={refs} />
            </Box>
            <Typography component="span" variant="body2" color="text.secondary" aria-label="changed to">→</Typography>
          </>
        )}
        <ChangeValue change={c} value={c.after} field={byKey[c.field]} refs={refs} />
      </Box>
    ))}
  </Stack>
)

const ICONS = {
  NOTE: ChatBubbleOutlineIcon, CREATED: AddIcon, UPDATED: EditOutlinedIcon, DELETED: DeleteOutlineIcon,
  FILE_ADDED: AttachFileIcon, FILE_REMOVED: AttachFileIcon, EMAIL: MailOutlineIcon, MERGED: MergeTypeIcon,
  DOCUMENT: DescriptionOutlinedIcon, SIGNATURE: DrawOutlinedIcon, MEETING: EventOutlinedIcon, CALL: PhoneOutlinedIcon, WHATSAPP: WhatsAppIcon, APPROVAL: FactCheckOutlinedIcon,
}

const summary = (item, entityType) => {
  const noun = entityLabel(entityType).toLowerCase()
  switch (item.kind) {
    case 'NOTE': return 'added a note'
    case 'CREATED': return `created this ${noun}`
    case 'DELETED': return `deleted this ${noun}`
    case 'FILE_ADDED': return 'attached a file'
    case 'FILE_REMOVED': return 'removed a file'
    case 'EMAIL': return item.email?.direction === 'IN' ? 'replied by email' : item.email?.status === 'FAILED' ? 'tried to send an email' : 'sent an email'
    case 'MERGED': return `merged a duplicate into this ${noun}`
    case 'DOCUMENT': return 'made a document'
    case 'SIGNATURE': return 'e-signature'
    case 'MEETING': return 'planned a meeting'
    case 'CALL': return 'logged a call'
    case 'WHATSAPP': return item.actor?.startsWith('WhatsApp:') ? 'wrote on WhatsApp' : 'sent a WhatsApp message'
    case 'APPROVAL': return item.body?.split(':')[0] || 'approval'
    case 'UPDATED': return item.changes?.length === 1 ? `changed ${item.changes[0].label}` : `changed ${item.changes?.length || 0} fields`
    default: return 'made a change'
  }
}

/** A meeting or call as it is now: time, status, call result, notes on demand. */
const MeetingEntry = ({ meeting }) => {
  const [open, setOpen] = useState(false)
  if (!meeting) return null
  const call = meeting.kind === 'CALL'
  const detail = call ? [meeting.callDirection === 'INCOMING' ? 'Incoming' : 'Outgoing', RESULT_LABEL[meeting.callResult]].filter(Boolean).join(' · ')
    : meeting.location
  return (
    <Paper variant="outlined" sx={{ mt: 0.75, px: 1.5, py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0, wordBreak: 'break-word',
          textDecoration: meeting.status === 'CANCELLED' ? 'line-through' : 'none' }}>{meeting.title}</Typography>
        {!call && <Chip size="small" variant="outlined" label={STATUS_LABEL[meeting.status]} color={meeting.status === 'DONE' ? 'success' : 'default'} />}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {whenLabel(meeting.startsAt, meeting.endsAt)}{detail ? ` · ${detail}` : ''}
      </Typography>
      {meeting.videoLink && meeting.status === 'PLANNED' && (
        <Link href={meeting.videoLink} target="_blank" rel="noopener noreferrer" variant="caption" sx={{ mr: 1.5 }}>Join</Link>
      )}
      {meeting.notes && (
        <>
          <Link component="button" type="button" variant="caption" onClick={() => setOpen((v) => !v)}>{open ? 'Hide notes' : 'Show notes'}</Link>
          <Collapse in={open} unmountOnExit>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.75 }}>{meeting.notes}</Typography>
          </Collapse>
        </>
      )}
    </Paper>
  )
}

/** An email as sent: recipients, subject, status, and the message on demand. */
const EmailEntry = ({ email, subject }) => {
  const [open, setOpen] = useState(false)
  if (!email) return <Typography variant="body2" sx={{ mt: 0.5 }}>{subject}</Typography>
  return (
    <Paper variant="outlined" sx={{ mt: 0.75, px: 1.5, py: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0, wordBreak: 'break-word' }}>{email.subject}</Typography>
        {email.direction === 'IN' && <Chip size="small" label="Reply" color="info" variant="outlined" />}
        {email.status !== 'SENT' && email.direction !== 'IN' && (
          <Tooltip title={email.error || ''}>
            <Chip size="small" label={email.status === 'FAILED' ? 'Not sent' : 'Sending'} color={email.status === 'FAILED' ? 'error' : 'default'} variant="outlined" />
          </Tooltip>
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
        {email.direction === 'IN' ? `From ${email.from}` : `To ${email.to.join(', ')}`}{email.cc.length > 0 ? ` · Cc ${email.cc.join(', ')}` : ''}
        {email.attachments.length > 0 ? ` · ${email.attachments.length} attachment${email.attachments.length === 1 ? '' : 's'}` : ''}
      </Typography>
      <Link component="button" type="button" variant="caption" onClick={() => setOpen((v) => !v)}>{open ? 'Hide message' : 'Show message'}</Link>
      <Collapse in={open} unmountOnExit>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.75 }}>{email.body}</Typography>
        {email.attachments.length > 0 && <Typography variant="caption" color="text.secondary">Attached: {email.attachments.join(', ')}</Typography>}
      </Collapse>
    </Paper>
  )
}

const Entry = ({ item, entityType, byKey, refs, onEdit, onDelete }) => {
  const nameOf = useNameOf()
  const [showValues, setShowValues] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState(null)
  const automated = item.source === 'AUTOMATION'
  const Icon = automated ? BoltIcon : (ICONS[item.kind] || EditOutlinedIcon)
  const actor = automated ? item.actor.replace(/^Automation: /, '') : nameOf(item.actor)
  const hasValues = item.changes?.length > 0

  return (
    <Box component="li" sx={{ display: 'flex', gap: 1.5, listStyle: 'none' }}>
      <Avatar sx={{ width: 28, height: 28, bgcolor: 'action.selected', color: 'text.secondary', mt: 0.25 }}>
        <Icon sx={{ fontSize: 16 }} />
      </Avatar>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0, wordBreak: 'break-word' }}>
            {automated && <Typography component="span" variant="body2" color="text.secondary">Automation </Typography>}
            <strong>{actor}</strong> {summary(item, entityType)}
            <Tooltip title={formatDate(item.createdOn)}>
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75, whiteSpace: 'nowrap' }}>
                {formatRelativeTime(item.createdOn)}{item.edited ? ' · edited' : ''}
              </Typography>
            </Tooltip>
          </Typography>
          {(item.canEdit || item.canDelete) && (
            <>
              <IconButton size="small" sx={{ mt: -0.5 }} aria-label="Note actions" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
                {item.canEdit && <MenuItem onClick={() => { setMenuAnchor(null); onEdit(item) }}>Edit</MenuItem>}
                {item.canDelete && <MenuItem onClick={() => { setMenuAnchor(null); onDelete(item) }}>Delete</MenuItem>}
              </Menu>
            </>
          )}
        </Box>

        {item.kind === 'NOTE' && (
          <Paper variant="outlined" sx={{ mt: 0.75, px: 1.5, py: 1 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.body}</Typography>
          </Paper>
        )}
        {item.kind === 'UPDATED' && <ChangeList changes={item.changes} byKey={byKey} refs={refs} />}
        {(item.kind === 'FILE_ADDED' || item.kind === 'FILE_REMOVED') && (
          <Typography variant="body2" sx={{ mt: 0.25, wordBreak: 'break-word', textDecoration: item.kind === 'FILE_REMOVED' ? 'line-through' : 'none', color: item.kind === 'FILE_REMOVED' ? 'text.secondary' : 'text.primary' }}>
            {item.body}
          </Typography>
        )}
        {item.kind === 'EMAIL' && <EmailEntry email={item.email} subject={item.body} />}
        {(item.kind === 'MEETING' || item.kind === 'CALL') && <MeetingEntry meeting={item.meeting} />}
        {item.kind === 'WHATSAPP' && (
          <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.body}</Typography>
        )}
        {item.kind === 'APPROVAL' && (
          <Typography variant="body2" sx={{ mt: 0.25, wordBreak: 'break-word' }}>{item.body?.split(':').slice(1).join(':').trim()}</Typography>
        )}
        {(item.kind === 'DOCUMENT' || item.kind === 'SIGNATURE') && (
          <Typography variant="body2" sx={{ mt: 0.25, wordBreak: 'break-word' }}>{item.body}</Typography>
        )}
        {item.kind === 'MERGED' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, wordBreak: 'break-word' }}>“{item.body}” (its notes, history, tasks, emails and files are here now)</Typography>
        )}
        {item.kind === 'CREATED' && hasValues && (
          <>
            <Link component="button" type="button" variant="caption" onClick={() => setShowValues((v) => !v)}>
              {showValues ? 'Hide initial values' : `Show initial values (${item.changes.length})`}
            </Link>
            <Collapse in={showValues} unmountOnExit><ChangeList changes={item.changes} byKey={byKey} refs={refs} created /></Collapse>
          </>
        )}
      </Box>
    </Box>
  )
}

const NoteEditor = ({ initial = '', busy, submitLabel, onSubmit, onCancel, autoFocus }) => {
  const [text, setText] = useState(initial)
  const trimmed = text.trim()
  const submit = () => { if (trimmed && trimmed.length <= MAX_NOTE) onSubmit(trimmed, () => setText('')) }
  return (
    <Box>
      <TextField
        multiline minRows={2} maxRows={10} size="small" placeholder="Add a note…" value={text} autoFocus={autoFocus}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit() } }}
        error={text.length > MAX_NOTE}
        helperText={text.length > MAX_NOTE ? `${text.length - MAX_NOTE} characters over the limit` : undefined}
        inputProps={{ 'aria-label': 'Note' }}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        <Button variant="contained" disabled={busy || !trimmed || text.length > MAX_NOTE} onClick={submit}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
      </Stack>
    </Box>
  )
}

/**
 * The record's history (who changed what, old → new; automation changes marked) and notes, newest
 * first. Anyone who can open the record can add notes; authors edit theirs, admins can delete any.
 */
const ActivityTimeline = ({ record, form }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [editing, setEditing] = useState(null)
  const [deleting, setDeleting] = useState(null)
  // under [tenant, 'records'] so every record save refreshes it too
  const queryKey = [tenantId, 'records', 'activity', record.id]
  const byKey = Object.fromEntries(allFields(form).map((f) => [f.fieldKey, f]))

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => activityAPI.list(record.id, pageParam, PAGE_SIZE),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
  })
  const items = data?.pages.flatMap((p) => p.items) || []
  const refs = Object.assign({}, ...(data?.pages.map((p) => p.refs) || []))

  const refresh = () => queryClient.invalidateQueries({ queryKey })
  const onError = (e) => notify(getErrorMessage(e), 'error')
  const addNote = useMutation({ mutationFn: (body) => activityAPI.addNote(record.id, body), onSuccess: refresh, onError })
  const editNote = useMutation({ mutationFn: ({ id, body }) => activityAPI.editNote(record.id, id, body), onSuccess: () => { setEditing(null); refresh() }, onError })
  const deleteNote = useMutation({ mutationFn: (id) => activityAPI.deleteNote(record.id, id), onSuccess: () => { setDeleting(null); refresh(); notify('Note deleted') }, onError })

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5 }}>Activity</Typography>
      <NoteEditor busy={addNote.isPending} submitLabel="Add note"
        onSubmit={(body, clear) => addNote.mutate(body, { onSuccess: clear })} />

      <Box component="ul" sx={{ p: 0, m: 0, mt: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }} aria-label="Activity">
        {isLoading && <Typography variant="body2" color="text.secondary">Loading activity…</Typography>}
        {error && <Typography variant="body2" color="error">{getErrorMessage(error)}</Typography>}
        {!isLoading && !error && items.length === 0 && (
          <Typography variant="body2" color="text.secondary">No activity yet.</Typography>
        )}
        {items.map((item) => (editing?.id === item.id ? (
          <Box component="li" key={item.id} sx={{ listStyle: 'none' }}>
            <NoteEditor initial={item.body} autoFocus busy={editNote.isPending} submitLabel="Save"
              onSubmit={(body) => editNote.mutate({ id: item.id, body })} onCancel={() => setEditing(null)} />
          </Box>
        ) : (
          <Entry key={item.id} item={item} entityType={record.entityType} byKey={byKey} refs={refs}
            onEdit={setEditing} onDelete={setDeleting} />
        )))}
      </Box>
      {hasNextPage && (
        <Button sx={{ mt: 1.5 }} onClick={() => fetchNextPage()} disabled={isFetchingNextPage}>
          {isFetchingNextPage ? 'Loading…' : 'Show older activity'}
        </Button>
      )}

      <ConfirmDialog
        open={!!deleting}
        title="Delete note?"
        message="The note will be removed from this record's activity."
        confirmLabel="Delete"
        destructive
        busy={deleteNote.isPending}
        onConfirm={() => deleteNote.mutate(deleting.id)}
        onClose={() => setDeleting(null)}
      />
    </Box>
  )
}

export default ActivityTimeline
